import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { switchMap, map, catchError, of, withLatestFrom, tap } from "rxjs";
import { ContractClient } from "src/api-service";
import { selectSelectedCompany } from "../../../../user/user.selectors";
import { normalizeError } from "../../../../app-store-error.types";
import { ContractManagementActions } from "./contract-management.actions";
import { ContractsActions } from "../../../contract/contracts/contracts.actions";

// **************************************************************************
// CONTRACT MANAGEMENT EFFECTS
// Admin CRUD operations for contracts
// Note: Load effect is in entities/contract/contracts
// **************************************************************************
@Injectable()
export class ContractManagementEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly contractClient = inject(ContractClient);

    createContract$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractManagementActions.createContract),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ request }, selectedCompany]) =>
                this.contractClient.create(selectedCompany!.id, request).pipe(
                    map(contract =>
                        ContractManagementActions.createContractSuccess({
                            contract,
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.CREATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractManagementActions.createContractFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.CREATE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    createContractSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractManagementActions.createContractSuccess),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            tap(([{ contract }, selectedCompany]) => {
                this.router.navigate([
                    "/",
                    selectedCompany!.id,
                    "contract-admin",
                    "contracts",
                    contract.id,
                ]);
            }),
            map(() => ContractsActions.load({})),
        ),
    );

    deleteContract$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractManagementActions.deleteContract),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id }, selectedCompany]) =>
                this.contractClient.delete(id, selectedCompany!.id).pipe(
                    map(() =>
                        ContractManagementActions.deleteContractSuccess({
                            id,
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(ContractManagementActions.deleteContractFailure({
                            error: normalizeError(error),
                            toastKey: "ADMIN.SUPER_USER.CONTRACTS.TOAST.DELETE_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    deleteContractSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractManagementActions.deleteContractSuccess),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            tap(([, selectedCompany]) => {
                this.router.navigate([
                    "/",
                    selectedCompany!.id,
                    "contract-admin",
                ]);
            }),
            map(() => ContractsActions.load({})),
        ),
    );
}
